import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, EmptyState, Field, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { Link, useNavigate, useParams } from "react-router";
import { recordCourseOpen } from "../lib/recentCourses";
import { demoCourses } from "../demo/data";
import { api, API_BASE_URL, isDemoMode } from "../api/client";
import { ColorPicker } from "../components/ColorPicker";
import { notifyDataChanged } from "../hooks/useCourses";
import type { Course } from "@nelurio/contracts";

const tabs = [
  ["overview", "course.overview"],
  ["resources", "course.resources"],
  ["assistant", "course.assistant"],
  ["flashcards", "course.flashcards"],
  ["notes", "course.notes"],
  ["quizzes", "course.quizzes"],
  ["progress", "course.progress"],
  ["reports", "course.reports"]
] as const;

interface CourseResource {
  id: string;
  title: string;
  kind: string;
  status: string;
  sizeBytes: number;
  createdAt: string;
}

interface Flashcard {
  id?: string;
  front: string;
  back: string;
  mastery?: number;
  source?: string;
}

interface QuizEntry {
  id?: string;
  question: string;
  answer: string;
  source?: string;
}

interface CourseNote {
  id: string;
  text: string;
  at: string;
}

function notesKey(courseId: string) {
  return `nelurio.notes.${courseId}`;
}

export function CourseDetailPage() {
  const { courseId } = useParams();
  const { t, formatDate, formatNumber, locale } = useI18n();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  // Demo mode seeds a fake course; REAL mode starts empty — a 404/stale link must
  // never render demo data (agent found real users seeing "Demo" on dead links).
  const [course, setCourse] = useState<Course | null>(() => (isDemoMode() ? demoCourses.find((item) => item.id === courseId) || demoCourses[0] : null));
  const [courseLoading, setCourseLoading] = useState(true);
  const [courseFailed, setCourseFailed] = useState(false);
  const [resources, setResources] = useState<CourseResource[]>([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const uploadRef = useRef<HTMLInputElement>(null);
  const [editOpen, setEditOpen] = useState(false);
  // Dashboard «درس‌های فعال» recency: register this open (device-local).
  useEffect(() => { if (courseId) recordCourseOpen(courseId); }, [courseId]);
  const [editTitle, setEditTitle] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editProfessor, setEditProfessor] = useState("");
  const [editExam, setEditExam] = useState("");
  const [editColor, setEditColor] = useState("#6c4cf6");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const loadCourse = () => {
    if (isDemoMode() || !courseId) return;
    setCourseLoading(true);
    setCourseFailed(false);
    api<Course>(`/courses/${courseId}`)
      .then((loaded) => { setCourse(loaded); setCourseLoading(false); })
      .catch(() => { setCourse(null); setCourseFailed(true); setCourseLoading(false); });
  };
  useEffect(loadCourse, [courseId]);

  // Switching courses must not carry the previous course's resource list.
  useEffect(() => { setResources([]); setResourcesLoaded(false); }, [courseId]);

  useEffect(() => {
    if (activeTab !== "resources" || !courseId || isDemoMode()) return;
    api<any[]>(`/resources?courseId=${encodeURIComponent(courseId)}`).then((data) => { setResources(data); setResourcesLoaded(true); }).catch(() => setResourcesLoaded(true));
  }, [activeTab, courseId]);

  const uploadResource = async (file: File) => {
    if (!courseId) return;
    setUploading(true);
    setUploadError("");
    try {
      if (!isDemoMode()) {
        const form = new FormData(); form.append("file", file); form.append("courseId", courseId); form.append("title", file.name);
        const created = await api<any>("/resources", { method: "POST", body: form });
        setResources((current) => [{ id: created.id, title: created.title, kind: created.kind, status: created.status, sizeBytes: created.sizeBytes, createdAt: created.createdAt }, ...current]);
      }
    } catch (reason) {
      setUploadError(reason instanceof Error ? reason.message : t("library.uploadError"));
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
      notifyDataChanged();
    }
  };

  // Null-guard after all hooks: real-mode fetch pending or 404 → no fake data.
  if (!course) {
    // courseFailed: the fetch REJECTED (network/404) — show retry, not a fake
    // eternal "loading". courseLoading: still fetching.
    return (
      <div className="page-stack rise">
        <EmptyState icon="book" title={courseFailed ? t("common.error") : t("common.loading")} description={courseFailed ? t("common.offline") : undefined} />
        {courseFailed && <Button onClick={loadCourse}>{t("common.retry")}</Button>}
        <div className="course-back">
          <Button variant="secondary" icon="arrow" onClick={() => navigate("/courses")}>{t("common.back")}</Button>
        </div>
      </div>
    );
  }

  const openEdit = () => {
    setEditTitle(course.title);
    setEditCode(course.code ?? "");
    setEditProfessor(course.professor ?? "");
    setEditExam(course.examDate ?? "");
    setEditColor(course.color || "#6c4cf6");
    setEditOpen(true);
  };

  const saveEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!courseId) return;
    setSaving(true);
    try {
      if (!isDemoMode()) {
        const updated = await api<Course>(`/courses/${courseId}`, {
          method: "PATCH",
          body: JSON.stringify({ title: editTitle.trim(), code: editCode, professor: editProfessor, examDate: editExam, color: editColor })
        });
        setCourse(updated);
      }
      setEditOpen(false);
    } catch (reason) {
      setEditError(reason instanceof Error ? reason.message : t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const deleteCourse = async () => {
    if (!courseId || isDemoMode()) { navigate("/courses"); return; }
    try {
      await api(`/courses/${courseId}`, { method: "DELETE" });
      notifyDataChanged();
      navigate("/courses");
    } catch {
      setConfirmDelete(false);
      setDeleteError(t("common.error"));
    }
  };

  return (
    <div className="page-stack rise">
      <PageHeader
        title={course.title}
        subtitle={`${course.code || ""}${course.professor ? ` · ${course.professor}` : ""}`}
        actions={
          <>
            <Button variant="secondary" icon="edit" onClick={openEdit} aria-label={t("common.edit")}>{t("common.edit")}</Button>
            <Button variant="danger" icon="trash" onClick={() => { setDeleteError(""); setConfirmDelete(true); }} aria-label={t("common.delete")}>{t("common.delete")}</Button>
            <Link to="/reports/new" className="nl-button nl-button--primary page-header__cta"><span>{t("report.short")}</span></Link>
          </>
        }
      />

      {confirmDelete && (
        <Card className="delete-confirm">
          <p>{t("course.deleteConfirm", { course: course.title })}</p>
          {deleteError && <div className="form-error" role="alert">{deleteError}</div>}
          <div className="delete-confirm__actions">
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" icon="trash" onClick={() => void deleteCourse()}>{t("common.delete")}</Button>
          </div>
        </Card>
      )}

      {editOpen && (
        <Card className="inline-form rise">
          <form onSubmit={saveEdit}>
            <div className="settings-grid">
              <Field label={t("course.titleField")} value={editTitle} onChange={(event) => setEditTitle(event.target.value)} required minLength={2} />
              <Field label={t("course.codeField")} value={editCode} onChange={(event) => setEditCode(event.target.value)} />
              <Field label={t("course.professorLabel")} value={editProfessor} onChange={(event) => setEditProfessor(event.target.value)} />
              <Field label={t("dashboard.exam")} type="date" value={editExam ?? ""} onChange={(event) => setEditExam(event.target.value)} />
              <div className="nl-field">
                <span className="nl-field__label">{t("course.colorField")}</span>
                <ColorPicker value={editColor} onChange={setEditColor} />
              </div>
            </div>
            {editError && <div className="form-error">{editError}</div>}
            <div className="feedback-card__footer">
              <Button type="submit" loading={saving}>{t("common.save")}</Button>
              <Button variant="secondary" onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
            </div>
          </form>
        </Card>
      )}

      <div className="course-back">
        <Button variant="secondary" icon="arrow" onClick={() => navigate("/courses")}>{t("common.back")}</Button>
      </div>

      <Card className="course-hero">
        <div className="course-hero__icon" style={{ background: course.color }}>{course.icon}</div>
        <div className="course-hero__copy">
          <span className="section-kicker">{course.code}</span>
          <h2>{course.title}</h2>
          <div><Badge tone="purple">{formatNumber(course.progress)}% {t("course.progress")}</Badge>{course.examDate && <Badge tone="coral">{t("dashboard.exam")} · {formatDate(course.examDate)}</Badge>}</div>
        </div>
        <div className="course-hero__ring" style={{ "--progress": `${course.progress * 3.6}deg` } as React.CSSProperties}><strong>{formatNumber(course.progress)}%</strong></div>
      </Card>

      <div className="course-tabs" role="tablist">
        {tabs.map(([id, key]) => <button key={id} className={activeTab === id ? "is-active" : ""} onClick={(event) => { setActiveTab(id); event.currentTarget.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); }}>{t(key)}</button>)}
      </div>

      {activeTab === "overview" && (
        <div className="course-overview-grid">
          <Card className="course-panel">
            <div className="section-heading"><div><span className="section-kicker">{t("common.today")}</span><h2>{t("dashboard.todayPlan")}</h2></div></div>
            <div className="mini-activity"><span><Icon name="sparkle" /></span><div><strong>{t("report.title")}</strong><small>{t("dashboard.newLesson")}</small></div><Link to="/reports/new" className="nl-button nl-button--secondary mini-activity__btn"><span>{t("common.add")}</span></Link></div>
            <div className="mini-activity"><span className="blue"><Icon name="timer" /></span><div><strong>{t("focus.title")}</strong><small>{t("focus.subtitle")}</small></div><Link to="/focus" className="nl-button nl-button--secondary mini-activity__btn"><span>{t("focus.start")}</span></Link></div>
            <div className="mini-activity"><span className="green"><Icon name="library" /></span><div><strong>{t("course.resources")}</strong><small>{t("course.resourcesHint", { count: formatNumber(course.resourceCount ?? 0) })}</small></div><Button variant="secondary" onClick={() => setActiveTab("resources")}>{t("common.open")}</Button></div>
          </Card>
          <Card className="course-panel">
            <div className="section-heading"><div><span className="section-kicker">{t("course.progress")}</span><h2>{t("course.facts")}</h2></div></div>
            <div className="course-facts">
              <div className="course-fact"><span>{t("dashboard.dueReviews")}</span><strong>{formatNumber(course.dueReviewCount ?? 0)}</strong></div>
              <div className="course-fact"><span>{t("course.resources")}</span><strong>{formatNumber(course.resourceCount ?? 0)}</strong></div>
              <div className="course-fact"><span>{t("course.professorLabel")}</span><strong>{course.professor || "—"}</strong></div>
              <div className="course-fact"><span>{t("dashboard.exam")}</span><strong>{course.examDate ? formatDate(course.examDate) : "—"}</strong></div>
            </div>
            <div className="topic-progress" key="progress">
              <div><strong>{t("course.progress")}</strong><small>{formatNumber(course.progress)}%</small></div>
              <span><i style={{ width: `${course.progress}%` }} /></span>
            </div>
          </Card>
        </div>
      )}
      {activeTab === "resources" && (
        <Card className="resource-table">
          <div className="course-resource-upload">
            <input ref={uploadRef} type="file" hidden onChange={async(event)=>{const file=event.target.files?.[0];if(!file)return;await uploadResource(file);}} />
            <Button icon="upload" loading={uploading} onClick={()=>uploadRef.current?.click()}>{t("library.upload")}</Button>
            <small>{course.title}</small>
          </div>
          {uploadError && <div className="form-error" role="alert">{uploadError}</div>}
          <div className="resource-table__header"><span>{t("library.name")}</span><span>{t("admin.status")}</span><span /></div>
          {resources.map((resource) => (
            <article className="resource-row" key={resource.id}>
              <span className="resource-row__file"><i><Icon name="file" /></i><div><strong>{resource.title}</strong><small>{resource.kind.toLocaleUpperCase(locale)} · {formatNumber(Math.max(1, Math.round(resource.sizeBytes / 1024)))} KB · {formatDate(resource.createdAt)}</small></div></span>
              <span><Badge tone={resource.status === "ready" ? "green" : resource.status === "failed" ? "coral" : "amber"}>{t(resource.status === "ready" ? "library.ready" : resource.status === "failed" ? "common.error" : "library.processing")}</Badge></span>
              <a href={`${API_BASE_URL}/resources/${resource.id}/content`} target="_blank" rel="noreferrer" aria-label={t("library.download")}><Icon name="arrow" /></a>
            </article>
          ))}
          {!resources.length && resourcesLoaded && <EmptyState icon="library" title={t("course.resourcesEmpty")} />}
        </Card>
      )}
      {activeTab === "flashcards" && courseId && <CourseFlashcards courseId={courseId} />}
      {activeTab === "quizzes" && courseId && <CourseQuiz courseId={courseId} />}
      {activeTab === "notes" && courseId && <CourseNotes courseId={courseId} />}
      {activeTab === "assistant" && courseId && <CourseAssistant courseId={courseId} courseTitle={course.title} />}
      {activeTab === "progress" && courseId && <CourseProgress course={course} courseId={courseId} onOpenTab={setActiveTab} />}
      {activeTab === "reports" && (
        <Card className="course-panel">
          <div className="section-heading"><div><span className="section-kicker">{t("course.reports")}</span><h2>{t("report.title")}</h2></div></div>
          <p className="tree-picker__hint">{t("report.subtitle")}</p>
          <Link to="/reports/new" className="nl-button nl-button--primary page-header__cta"><span>{t("report.short")}</span></Link>
        </Card>
      )}
    </div>
  );
}

/** Flip-card study mode: AI-generated + manual cards, deletable. */
function CourseFlashcards({ courseId }: { courseId: string }) {
  const { t, locale, formatNumber } = useI18n();
  const [cards, setCards] = useState<Flashcard[] | null>(null);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState<Set<string>>(new Set());
  const [extracting, setExtracting] = useState(false);
  const [notice, setNotice] = useState("");

  const load = () => {
    if (isDemoMode()) { setCards([]); return; }
    api<Flashcard[]>(`/courses/${courseId}/flashcards`).then(setCards).catch(() => setCards([]));
  };
  useEffect(load, [courseId]);

  const extractWithAI = async () => {
    setExtracting(true);
    setNotice("");
    try {
      const result = await api<{ flashcards: number; quiz: number }>("/ai/extract", { method: "POST", body: JSON.stringify({ courseId, locale }) });
      setNotice(t("study.extracted", { cards: result.flashcards, quiz: result.quiz }));
      load();
      notifyDataChanged();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : t("common.error"));
    } finally {
      setExtracting(false);
    }
  };

  const removeCard = async (card: Flashcard) => {
    // Topic-derived cards have no server row — they disappear with the topic.
    if (!card.id || card.source === "topic") return;
    setCards((current) => (current ?? []).filter((item) => item.id !== card.id));
    // Keep the counter inside the shortened deck ("5 / 4" bug).
    setIndex((value) => Math.max(0, Math.min(value, (cards ?? []).length - 2)));
    await api(`/flashcards/${card.id}`, { method: "DELETE" }).catch(() => load());
  };

  if (cards === null) return <Card><EmptyState icon="sparkle" title={t("common.loading")} /></Card>;

  const card = cards[Math.min(index, Math.max(0, cards.length - 1))];

  return (
    <Card className="flashcard-panel">
      <div className="section-heading compact">
        <div><span className="section-kicker">{t("course.flashcards")}</span><h2>{cards.length ? t("flash.progress", { current: formatNumber(Math.min(index, cards.length - 1) + 1), total: formatNumber(cards.length) }) : t("flash.emptyTitle")}</h2></div>
        <Button variant="secondary" icon="sparkle" loading={extracting} onClick={() => void extractWithAI()}>{t("study.generateAI")}</Button>
      </div>
      {notice && <p className="tree-picker__hint">{notice}</p>}
      {!cards.length ? (
        <EmptyState icon="sparkle" title={t("flash.emptyTitle")} description={t("flash.emptyBody")} action={<Button icon="sparkle" loading={extracting} onClick={() => void extractWithAI()}>{t("study.generateAI")}</Button>} />
      ) : (
        <>
          <button className={`flashcard ${flipped ? "is-flipped" : ""}`} onClick={() => setFlipped((value) => !value)} aria-label={t("flash.flip")}>
            <span className="flashcard__face flashcard__front"><small>{t("flash.question")}</small><strong>{card.front}</strong></span>
            <span className="flashcard__face flashcard__back"><small>{t("flash.answer")}</small><p>{card.back || t("flash.noBack")}</p></span>
          </button>
          {card?.id && card.source !== "topic" && (
            <div className="flashcard__meta">
              <button onClick={() => { void removeCard(card); }} aria-label={t("common.delete")}><Icon name="trash" size={14} />{t("common.delete")}</button>
            </div>
          )}
          <div className="flashcard__controls">
            <Button variant="secondary" icon="chevron" className="flip-prev" onClick={() => { setFlipped(false); setIndex((value) => (value - 1 + cards.length) % cards.length); }} aria-label={t("tour.back")} />
            <Button variant="secondary" onClick={() => setFlipped((value) => !value)}>{t("flash.flip")}</Button>
            <Button variant="danger" onClick={() => { setFlipped(false); setIndex((value) => (value + 1) % cards.length); }}>{t("flash.again")}</Button>
            <Button icon="check" onClick={() => { setKnown((current) => new Set(current).add(card.front)); setFlipped(false); setIndex((value) => (value + 1) % cards.length); }}>{t("flash.knownBtn")}</Button>
            <Button variant="secondary" icon="chevron" className="flip-next" onClick={() => { setFlipped(false); setIndex((value) => (value + 1) % cards.length); }} aria-label={t("tour.next")} />
          </div>
          <p className="task-column__empty">{t("flash.hint", { count: formatNumber(known.size) })}</p>
        </>
      )}
    </Card>
  );
}

/** Self-quiz: AI-generated items from the course content + manual questions. */
function CourseQuiz({ courseId }: { courseId: string }) {
  const { t, locale, formatNumber } = useI18n();
  const [items, setItems] = useState<QuizEntry[] | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [extracting, setExtracting] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [notice, setNotice] = useState("");

  const load = () => {
    if (isDemoMode()) { setItems([]); return; }
    api<QuizEntry[]>(`/courses/${courseId}/quiz`).then(setItems).catch(() => setItems([]));
  };
  useEffect(load, [courseId]);

  const extractWithAI = async () => {
    setExtracting(true);
    setNotice("");
    try {
      const result = await api<{ flashcards: number; quiz: number }>("/ai/extract", { method: "POST", body: JSON.stringify({ courseId, locale }) });
      setNotice(t("study.extracted", { cards: result.flashcards, quiz: result.quiz }));
      load();
      notifyDataChanged();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : t("common.error"));
    } finally {
      setExtracting(false);
    }
  };

  const addManual = async (event: React.FormEvent) => {
    event.preventDefault();
    if (manualSaving || !question.trim()) return;
    setManualSaving(true);
    try {
      await api(`/courses/${courseId}/quiz`, { method: "POST", body: JSON.stringify({ question: question.trim(), answer: answer.trim() }) });
      setQuestion("");
      setAnswer("");
      setManualOpen(false);
      load();
    } catch {
      setNotice(t("common.error"));
    } finally {
      setManualSaving(false);
    }
  };

  const removeItem = async (item: QuizEntry) => {
    if (!item.id) return;
    setItems((current) => (current ?? []).filter((entry) => entry.id !== item.id));
    await api(`/quiz/${item.id}`, { method: "DELETE" }).catch(() => load());
  };

  if (items === null) return <Card><EmptyState icon="check" title={t("common.loading")} /></Card>;

  // Correct = the chosen option equals the item's topic (the "is-correct" branch
  // below) — counting merely-answered questions painted the badge green on all-wrong runs.
  const correctCount = items.filter((item) => answers[item.question] === item.question).length;

  return (
    <Card className="quiz-panel">
      <div className="section-heading compact">
        <div><span className="section-kicker">{t("course.quizzes")}</span><h2>{t("quiz.title")}</h2></div>
        <div className="quiz-head-actions">
          <Badge tone={correctCount >= Math.max(1, items.length) / 2 ? "green" : "amber"}>{formatNumber(correctCount)} / {formatNumber(items.length)}</Badge>
          <Button variant="secondary" icon="sparkle" loading={extracting} onClick={() => void extractWithAI()}>{t("study.generateAI")}</Button>
          <Button variant="secondary" icon="plus" onClick={() => setManualOpen((value) => !value)}>{t("quiz.manualAdd")}</Button>
        </div>
      </div>
      {notice && <p className="tree-picker__hint">{notice}</p>}
      {manualOpen && (
        <form className="notes-form quiz-manual" onSubmit={addManual}>
          <label className="nl-field"><span className="nl-field__label">{t("quiz.questionField")}</span>
            <textarea value={question} onChange={(event) => setQuestion(event.target.value)} rows={2} required />
          </label>
          <label className="nl-field"><span className="nl-field__label">{t("quiz.answerField")}</span>
            <textarea value={answer} onChange={(event) => setAnswer(event.target.value)} rows={2} />
          </label>
          <Button type="submit" icon="plus">{t("common.add")}</Button>
        </form>
      )}
      {!items.length ? (
        <EmptyState icon="check" title={t("quiz.emptyTitle")} description={t("quiz.emptyBody")} action={<Button icon="sparkle" loading={extracting} onClick={() => void extractWithAI()}>{t("study.generateAI")}</Button>} />
      ) : (
        items.map((item, qIndex) => {
          const chosen = answers[item.question];
          return (
            <div className="quiz-item" key={item.id ?? item.question}>
              <strong>{formatNumber(qIndex + 1)}. {t("quiz.whichTopic")}</strong>
              <p>{item.answer || t("quiz.noBack")}</p>
              <div className="quiz-options">
                {optionsFor(items, qIndex).map((option) => {
                  const isChosen = chosen === option;
                  const state = chosen === undefined ? "" : option === item.question ? "is-correct" : isChosen ? "is-wrong" : "";
                  return (
                    <button key={option} className={`quiz-option ${state}`} disabled={chosen !== undefined}
                      onClick={() => setAnswers((current) => ({ ...current, [item.question]: option }))}>
                      {option}
                    </button>
                  );
                })}
              </div>
              {item.id && (
                <div className="flashcard__meta">
                  <button onClick={() => void removeItem(item)} aria-label={t("common.delete")}><Icon name="trash" size={14} />{t("common.delete")}</button>
                </div>
              )}
            </div>
          );
        })
      )}
    </Card>
  );
}

/** Question = correct option, distractors come from other items' questions. */
// Shuffle seeded by the item's question text: stable for the life of the item —
  // a re-render used to reshuffle and make the chosen answer jump under the cursor.
function optionsFor(items: QuizEntry[], index: number): string[] {
  const correct = items[index].question;
  const others = items.filter((_, i) => i !== index).map((item) => item.question).slice(0, 3);
  const options = [correct, ...others];
  let seed = 0;
  for (const ch of correct) seed = (seed * 31 + ch.charCodeAt(0)) & 0xffff;
  for (let k = options.length - 1; k > 0; k -= 1) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const swap = seed % (k + 1);
    [options[k], options[swap]] = [options[swap], options[k]];
  }
  return options;
}

/** Course-local quick notes, stored on the server. */
function CourseNotes({ courseId }: { courseId: string }) {
  const { t, formatDate } = useI18n();
  const [notes, setNotes] = useState<CourseNote[] | null>(null);
  const [draft, setDraft] = useState("");

  const load = () => {
    if (isDemoMode()) { setNotes([]); return; }
    api<CourseNote[]>(`/courses/${courseId}/notes`).then(setNotes).catch(() => setNotes([]));
  };
  useEffect(load, [courseId]);

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || isDemoMode()) return;
    // Clear only after success — a failed POST used to silently eat the note.
    try {
      await api(`/courses/${courseId}/notes`, { method: "POST", body: JSON.stringify({ text }) });
      setDraft("");
      load();
    } catch {
      load();
    }
  };

  const remove = async (note: CourseNote) => {
    setNotes((current) => (current ?? []).filter((item) => item.id !== note.id));
    if (!isDemoMode()) await api(`/notes/${note.id}`, { method: "DELETE" }).catch(() => load());
  };

  if (notes === null) return <Card><EmptyState icon="edit" title={t("common.loading")} /></Card>;

  return (
    <Card className="notes-panel">
      <div className="section-heading compact"><div><span className="section-kicker">{t("course.notes")}</span><h2>{t("notes.title")}</h2></div></div>
      <form className="notes-form" onSubmit={add}>
        <textarea value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={t("notes.placeholder")} rows={3} />
        <Button type="submit" icon="plus">{t("common.add")}</Button>
      </form>
      {notes.map((note) => (
        <article className="notes-item" key={note.id}>
          <p>{note.text}</p>
          <footer>
            <small>{formatDate(note.at, { dateStyle: "short", timeStyle: "short" })}</small>
            <button onClick={() => void remove(note)} aria-label={t("common.delete")}><Icon name="trash" size={14} /></button>
          </footer>
        </article>
      ))}
      {!notes.length && <EmptyState icon="edit" title={t("notes.emptyTitle")} description={t("notes.emptyBody")} />}
    </Card>
  );
}

/** Course-pinned AI chat, grounded in this course's resources. */
function CourseAssistant({ courseId, courseTitle }: { courseId: string; courseTitle: string }) {
  const { t, locale } = useI18n();
  const [messages, setMessages] = useState<Array<{ role: "user" | "ai"; text: string; saved?: string }>>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    const question = draft.trim();
    if (!question || busy) return;
    setDraft("");
    setMessages((current) => [...current, { role: "user", text: question }]);
    setBusy(true);
    setError("");
    try {
      const answer = await api<{ text: string }>("/ai/messages", { method: "POST", body: JSON.stringify({ courseId, courseTitle, message: question, locale, allowWebSearch: false }) });
      setMessages((current) => [...current, { role: "ai", text: answer.text }]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  // Khoze hands the answer to the study engine: flashcards and quiz land in
  // their sections, the summary becomes a note.
  const saveToCourse = async (index: number) => {
    const answer = messages[index];
    if (!answer || answer.saved) return;
    try {
      const result = await api<{ flashcards: number; quiz: number }>("/ai/extract", { method: "POST", body: JSON.stringify({ courseId, text: answer.text, locale }) });
      setMessages((current) => current.map((message, i) => (i === index ? { ...message, saved: t("study.extracted", { cards: result.flashcards, quiz: result.quiz }) } : message)));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("common.error"));
    }
  };

  return (
    <Card className="course-panel assistant-panel">
      <div className="section-heading compact"><div><span className="section-kicker">{courseTitle}</span><h2>{t("ai.title")}</h2></div></div>
      <div className="assistant-thread">
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            <div className={`assistant-message ${message.role === "user" ? "is-user" : ""}`}>{message.text}</div>
            {message.role === "ai" && (
              <div className="assistant-save">
                {message.saved
                  ? <Badge tone="green">{message.saved}</Badge>
                  : <Button variant="secondary" icon="plus" onClick={() => void saveToCourse(index)}>{t("study.saveToCourse")}</Button>}
              </div>
            )}
          </React.Fragment>
        ))}
        {busy && <div className="assistant-message">{t("common.loading")}</div>}
        {!messages.length && !busy && <EmptyState icon="sparkle" title={t("ai.title")} description={t("ai.placeholder")} />}
      </div>
      {error && <div className="form-error" role="alert">{error}</div>}
      <form className="notes-form" onSubmit={send}>
        <textarea value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={t("ai.placeholder")} rows={2} />
        <Button type="submit" icon="sparkle" loading={busy}>{t("ai.send")}</Button>
      </form>
      <p className="tree-picker__hint">{t("ai.generatedNotice")}</p>
    </Card>
  );
}

/** Progress = readiness. Four concrete levers drive it; each one carries the
 *  action that moves it, so the user always knows what to do next. */
function CourseProgress({ course, courseId, onOpenTab }: { course: Course; courseId: string; onOpenTab: (tab: string) => void }) {
  const { t, locale, formatNumber } = useI18n();
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [quiz, setQuiz] = useState<QuizEntry[]>([]);

  useEffect(() => {
    if (isDemoMode()) return;
    api<Flashcard[]>(`/courses/${courseId}/flashcards`).then(setCards).catch(() => undefined);
    api<QuizEntry[]>(`/courses/${courseId}/quiz`).then(setQuiz).catch(() => undefined);
  }, [courseId]);

  const levers = [
    {
      key: "reviews",
      done: (course.dueReviewCount ?? 0) === 0,
      value: formatNumber(Math.max(0, course.dueReviewCount ?? 0)),
      label: t("lever.reviews"),
      logic: t("lever.reviewsLogic"),
      action: { label: t("lever.reviewsAction"), onClick: () => onOpenTab("overview") }
    },
    {
      key: "materials",
      done: (course.resourceCount ?? 0) >= 3,
      value: `${formatNumber(Math.min(course.resourceCount ?? 0, 3))}/${formatNumber(3)}`,
      label: t("lever.materials"),
      logic: t("lever.materialsLogic"),
      action: { label: t("lever.materialsAction"), onClick: () => onOpenTab("resources") }
    },
    {
      key: "cards",
      done: cards.length >= 10,
      value: `${formatNumber(Math.min(cards.length, 10))}/${formatNumber(10)}`,
      label: t("lever.cards"),
      logic: t("lever.cardsLogic"),
      action: { label: t("lever.cardsAction"), onClick: () => onOpenTab("flashcards") }
    },
    {
      key: "quiz",
      done: quiz.length >= 3,
      value: `${formatNumber(Math.min(quiz.length, 3))}/${formatNumber(3)}`,
      label: t("lever.quiz"),
      logic: t("lever.quizLogic"),
      action: { label: t("lever.quizAction"), onClick: () => onOpenTab("quizzes") }
    }
  ];
  const score = Math.round((levers.filter((lever) => lever.done).length / levers.length) * 100);

  return (
    <Card className="course-panel progress-levers">
      <div className="section-heading"><div><span className="section-kicker">{t("course.progress")}</span><h2>{t("lever.title")}</h2></div></div>
      <p className="tree-picker__hint">{t("lever.lead")}</p>
      <div className="progress-levers__ring" style={{ "--progress": `${score * 3.6}deg` } as React.CSSProperties}>
        <strong>{formatNumber(score)}%</strong>
        <small>{t("lever.ready")}</small>
      </div>
      <div className="levers-list">
        {levers.map((lever) => (
          <article className={`lever ${lever.done ? "is-done" : ""}`} key={lever.key}>
            <span className="lever__state">{lever.done ? "\u2713" : ""}</span>
            <div className="lever__body">
              <strong>{lever.label}</strong>
              <small>{lever.logic}</small>
            </div>
            <Badge tone={lever.done ? "green" : "amber"}>{lever.value}</Badge>
            {!lever.done && <Button variant="secondary" onClick={lever.action.onClick}>{lever.action.label}</Button>}
          </article>
        ))}
      </div>
    </Card>
  );
}
